import { useModal } from '@ebay/nice-modal-react';
import { Preset } from 'matrix-js-sdk/src';
import { MatrixClient } from 'matrix-js-sdk/src/matrix';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { buildCreateMeetingPayload } from 'api/meedioBackend/adapters';
import { createMeeting } from 'api/meedioBackend/createMeeting';
import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import { UpdateMeetingRequest } from 'api/meedioBackend/types';
import { updateMeeting } from 'api/meedioBackend/updateMeeting';
import { useCalendarSyncContext } from 'contexts/CalendarSyncProvider/CalendarSyncProvider';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useCreateMeeting from 'hooks/useCreateMeeting/useCreateMeeting';
import useMeetingPermissionsForRoom from 'hooks/useMeetingPermissionsForRoom/useMeetingPermissionsForRoom';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { AuthTokens } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/authHelpers';
import {
  deleteEventFromCalendar,
  getEventsByRoomId,
  getPrimaryCalendarInformation,
  updateEventInCalendar,
} from 'modules/CalendarView/CalendarSync/CalendarSyncModal/calendarHelpers';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { getRoomMetadata } from 'modules/RoomsListItem/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import {
  buildCanonicalParentEvent,
  CustomMeedioMatrixEvents,
  getViaServers,
  isPrivateRoom as checkIsPrivateRoom,
} from 'utils/matrixUtils';
import { canSendMeetingRoomStateEvent } from 'utils/meetingPermissionGates';
import { logAndSendToSentry } from 'utils/utils';

import { sendScheduleStateEvent } from './sendScheduleStateEvent';
import useEditMatrixRoom from './useEditMatrixRoom';
import { CreateRoomProps, getFullDateAt, UpdateRoomProps } from './utils';
import useCreateNewRoomDataContext from '../CreateNewRoomDataProvider/useCreateNewRoomDataContext';
import { extractTimeZone, getRandomFourDigitString, RoomFormType } from '../CreateNewRoomDataProvider/utils';

type MeetingCalendarInfo = {
  eventId?: string;
  primaryCalendarId?: string;
  token?: AuthTokens;
};

const leaveMeetingRooms = async (
  matrixClient: MatrixClient,
  spaceRoomId: string,
  chatRoomId: string,
  calendarInfo?: MeetingCalendarInfo
) => {
  try {
    await matrixClient.leave(spaceRoomId);
  } catch (error) {
    logAndSendToSentry('Failed to leave meeting space during creation cleanup', { error, roomId: spaceRoomId });
  }

  try {
    await matrixClient.leave(chatRoomId);
  } catch (error) {
    logAndSendToSentry('Failed to leave meeting chat during creation cleanup', { error, roomId: chatRoomId });
  }

  const { eventId, primaryCalendarId, token } = calendarInfo ?? {};
  if (eventId && primaryCalendarId && token) {
    deleteEventFromCalendar(storageUtils.getProviderFromToken(token), primaryCalendarId, eventId, token.access_token);
  }
};

const useCreateRoomStep = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { remove } = useModal();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const { matrixClient } = useMatrixContext();
  const { workspaceId, workspacePermissions } = useWorkspacesContext();
  const { createMeeting: createMatrixMeeting } = useCreateMeeting();
  const {
    isScheduling,
    roomToEdit,
    digitsPostfix,
    defaultEmails,
    defaultInvitations,
    setDigitsPostfix,
    roomForm: {
      trigger,
      handleSubmit,
      watch,
      formState: { dirtyFields },
    },
  } = useCreateNewRoomDataContext();
  const { editRoom } = useEditMatrixRoom(dirtyFields, { room: roomToEdit ?? null });
  const [isLoading, setIsLoading] = useState(false);
  const { mainCalendarInfo, accountCalendars } = useCalendarSyncContext();
  const meetingPermissions = useMeetingPermissionsForRoom(roomToEdit ?? null);

  const isPrivateRoomValue = watch('isPrivateRoom');
  const metadata = roomToEdit && getRoomMetadata(roomToEdit);
  const isPrivateMeeting = roomToEdit ? checkIsPrivateRoom(roomToEdit) : false;

  const canEditWaitingListOnEdit =
    !!roomToEdit &&
    !isScheduling &&
    meetingPermissions.canToggleWaitingList &&
    (!isPrivateMeeting || meetingPermissions.canEditMeetingDetails);

  const hasNonWaitingListDirtyFields = () =>
    (Object.keys(dirtyFields) as (keyof typeof dirtyFields)[]).some(
      (field) => field !== 'isWaitingListEnabled' && !!dirtyFields[field]
    );

  const createRoom = async ({
    roomName,
    roomAlias,
    isWaitingListEnabled,
    startsAt,
    endsAt,
    description,
    participants,
    invitationMessage,
  }: CreateRoomProps) => {
    setIsLoading(true);

    const isScheduled = startsAt && endsAt;
    const preset = isScheduled ? Preset.PrivateChat : Preset.PublicChat;

    const customInitialStates = workspaceId
      ? [buildCanonicalParentEvent(workspaceId, getViaServers(workspaceId, matrixClient))]
      : [];

    const response = await createMatrixMeeting({
      alias: roomAlias,
      isWaitingListEnabled,
      isPrivateRoom: isPrivateRoomValue,
      description,
      title: roomName,
      preset,
      customInitialStates,
    });

    if (!response) return setIsLoading(false);

    const {
      space: newRoom,
      chat: chatRoom,
      calendarInfo: { eventId, token, primaryCalendarId },
    } = response;

    const isTeamMeeting = !!workspaceId && !isWaitingListEnabled && !isPrivateRoomValue;

    const payload = buildCreateMeetingPayload(
      newRoom.room_id,
      startsAt ?? undefined,
      endsAt ?? undefined,
      invitationMessage,
      roomName,
      roomAlias,
      isScheduled ? participants : undefined,
      { isWaitingListEnabled, workspaceMatrixRoomId: workspaceId, isTeamMeeting }
    );

    try {
      await createMeeting(payload);
    } catch (error) {
      logger.error('Backend meeting creation failed after Matrix rooms were created', error);
      await leaveMeetingRooms(matrixClient, newRoom.room_id, chatRoom.room_id, { eventId, token, primaryCalendarId });
      setDigitsPostfix(getRandomFourDigitString());
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
      return setIsLoading(false);
    }

    try {
      if (isScheduled) {
        await sendScheduleStateEvent({ client: matrixClient, roomId: newRoom.room_id, startsAt, endsAt });
        logger.info('update event step:', { primaryCalendarId, eventId, hasToken: !!token });
        if (primaryCalendarId && eventId && token) {
          const provider = storageUtils.getProviderFromToken(token);
          logger.info('calendar event update started', { primaryCalendarId, startsAt, endsAt });
          await updateEventInCalendar(
            provider,
            primaryCalendarId,
            eventId,
            { startDate: startsAt, endDate: endsAt },
            token.access_token
          ).then((res) => {
            if (res.error) {
              logger.error('Failed to update calendar event', res.error);
              pushToast({ variant: 'error', title: t('calendar_event_update_error') });
            }
          });
        }

        // Matrix 3PID invites still required so Synapse writes the
        // m.room.third_party_invite state event. The backend pre-registered
        // each guest via add_guests, so storeInvite takes the find-existing
        // branch and does not send a duplicate invitation email.
        for (const email of participants) {
          await matrixClient.inviteByEmail(newRoom.room_id, email).catch((error) => {
            logger.error('inviteByEmail has failed', error);
            pushToast({ variant: 'info', title: t('participant_not_invited', { email }) });
          });
        }
      }

      remove();
      if (!isScheduled) navigate(`/rooms/${roomAlias}`);
    } catch (error) {
      await matrixClient.leave(newRoom.room_id);
      setDigitsPostfix(getRandomFourDigitString());
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    } finally {
      setIsLoading(false);
    }
  };

  const updateRoom = async ({
    room,
    startsAt,
    endsAt,
    invitationMessage,
    roomAlias,
    participants,
    roomName,
    description,
    timeZone,
    ...props
  }: UpdateRoomProps) => {
    setIsLoading(true);

    const isTimeZoneChanged = metadata?.scheduledToStartAt && timeZone !== extractTimeZone(metadata.scheduledToStartAt);
    const isStartsAtChanged = !moment(metadata?.scheduledToStartAt).isSame(startsAt);
    const isEndsAtChanged = !moment(metadata?.scheduledToEndAt).isSame(endsAt);
    const isRoomRescheduled = !!startsAt && !!endsAt && (isStartsAtChanged || isEndsAtChanged || isTimeZoneChanged);
    const newParticipants = participants.filter((email) => !defaultEmails.includes(email));
    const missingParticipants = defaultEmails.filter((email) => !participants.includes(email));
    const removeInvitationIds = missingParticipants
      .map((email) => {
        const invitationId = defaultInvitations[email];
        if (!invitationId) logger.warn('No invitation id mapped for removed email', { email });
        return invitationId;
      })
      .filter((id): id is string => !!id);

    try {
      await editRoom({
        room,
        roomName,
        roomAlias,
        participants,
        description,
        isPrivateRoom: isPrivateRoomValue,
        ...props,
      });

      const actorUserId = matrixClient.getUserId();
      const canEditDetails = meetingPermissions.canEditMeetingDetails;
      const canToggleWaitingList = meetingPermissions.canToggleWaitingList;

      const { primaryCalendarId, token, isTokenExpired } = getPrimaryCalendarInformation(
        accountCalendars,
        mainCalendarInfo
      );

      if (canEditDetails && primaryCalendarId && token && !isTokenExpired) {
        const provider = storageUtils.getProviderFromToken(token);
        const eventsResponse = await getEventsByRoomId(provider, primaryCalendarId, room.roomId, token.access_token);
        const event = eventsResponse.data?.[0];

        if (event) {
          const provider = storageUtils.getProviderFromToken(token);
          const meetingUrl = roomAlias ? `${window.location.origin}/rooms/${roomAlias}` : event.location;

          await updateEventInCalendar(
            provider,
            primaryCalendarId,
            event.id || '',
            {
              title: roomName,
              description: description || event.description,
              location: meetingUrl,
              startDate: startsAt || event.startDate,
              endDate: endsAt || event.endDate,
            },
            token.access_token
          ).then((res) => {
            if (res.error) {
              logger.error('Unable to update existing event', res.error);
              pushToast({ variant: 'error', title: t('calendar_event_update_error') });
            }
          });
        }
      }

      if (
        canEditDetails &&
        isRoomRescheduled &&
        actorUserId &&
        canSendMeetingRoomStateEvent(room, actorUserId, CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT)
      ) {
        await sendScheduleStateEvent({ client: matrixClient, roomId: room.roomId, startsAt, endsAt });
      } else if (canEditDetails && isRoomRescheduled) {
        pushNoPermissionToast();
      }

      const backendPayload: UpdateMeetingRequest = {
        ...(canEditDetails && startsAt && isStartsAtChanged && { scheduled_at: startsAt }),
        ...(canEditDetails && endsAt && isEndsAtChanged && { ends_at: endsAt }),
        ...(canEditDetails && dirtyFields.invitationMessage && { message: invitationMessage }),
        ...(canEditDetails && dirtyFields.roomName && { room_name: roomName }),
        ...(canEditDetails && dirtyFields.roomAlias && { alias: roomAlias }),
        ...(dirtyFields.isWaitingListEnabled &&
          canToggleWaitingList && { waiting_list_enabled: props.isWaitingListEnabled }),
        ...(canEditDetails && removeInvitationIds.length > 0 && { remove_invitations: removeInvitationIds }),
        ...(canEditDetails && newParticipants.length > 0 && { add_guests: newParticipants }),
      };

      if (Object.keys(backendPayload).length > 0) {
        const meetingResponse = await getMeetingByRoomId(room.roomId);
        if (meetingResponse) {
          await updateMeeting(meetingResponse.id, backendPayload);
        } else {
          logger.error('Meeting not found when updating scheduled room', { roomId: room.roomId });
        }
      }

      // Matrix 3PID invites still required for each new guest so Synapse
      // writes the m.room.third_party_invite state event. The backend
      // pre-registered these addresses via add_guests, so storeInvite
      // takes the find-existing branch and avoids duplicate emails.
      if (canEditDetails && newParticipants.length) {
        for (const email of newParticipants) await matrixClient.inviteByEmail(room.roomId, email);
      }

      remove();
    } catch (error) {
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = handleSubmit(
    ({ roomAlias, startDate, startTime, endDate, endTime, timeZone, isWaitingListEnabled, ...props }: RoomFormType) => {
      if (roomToEdit) {
        const wantsWaitingListChange = !!dirtyFields.isWaitingListEnabled;
        const wantsOtherChanges = hasNonWaitingListDirtyFields();

        if (wantsOtherChanges && !meetingPermissions.canEditMeetingDetails) {
          pushNoPermissionToast();
          return;
        }

        if (wantsWaitingListChange && !canEditWaitingListOnEdit) {
          pushNoPermissionToast();
          return;
        }

        const alias = `${roomAlias}-${digitsPostfix}`;
        const startsAt = startDate && startTime && isScheduling ? getFullDateAt(startDate, startTime, timeZone) : null;
        const endsAt = endDate && endTime && isScheduling ? getFullDateAt(endDate, endTime, timeZone) : null;

        return updateRoom({
          room: roomToEdit,
          roomAlias: alias,
          startsAt,
          endsAt,
          timeZone,
          isWaitingListEnabled,
          ...props,
        });
      }

      const isScheduled = startDate || startTime || endDate || endTime;
      const isInstantMeeting = !isScheduled && !isPrivateRoomValue && !isWaitingListEnabled;
      const hasNoPermissionForInstantMeeting = isInstantMeeting && !workspacePermissions.createInstantMeeting;
      const hasNoPermissionForScheduling = !workspacePermissions.createScheduledMeeting && isScheduled;
      const hasNoPermissionForWaitingList = !workspacePermissions.createWaitingListMeeting && isWaitingListEnabled;
      const hasNoPermissionForPrivateRoom = !workspacePermissions.createPrivateMeeting && isPrivateRoomValue;
      if (
        hasNoPermissionForInstantMeeting ||
        hasNoPermissionForScheduling ||
        hasNoPermissionForWaitingList ||
        hasNoPermissionForPrivateRoom
      ) {
        pushNoPermissionToast();
        return;
      }

      const alias = `${roomAlias}-${digitsPostfix}`;

      const startsAt = startDate && startTime && isScheduling ? getFullDateAt(startDate, startTime, timeZone) : null;
      const endsAt = endDate && endTime && isScheduling ? getFullDateAt(endDate, endTime, timeZone) : null;
      const defaultProps: CreateRoomProps = {
        roomAlias: alias,
        startsAt,
        endsAt,
        timeZone,
        isWaitingListEnabled,
        ...props,
      };

      return createRoom(defaultProps);
    },
    () => trigger()
  );

  return { handleFormSubmit, isLoading };
};

export default useCreateRoomStep;
